import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom, Observable } from 'rxjs';
import { ConfirmationDialog } from '../confirmation-dialog/confirmation-dialog';
import { GroupInputComponent } from '../group-input/group-input';
import { I18nPipe } from '../../i18n/i18n-pipe';
import { I18nScopeReadyDirective } from '../../i18n/i18n-scope-ready.directive';
import { provideI18nScope } from '../../i18n/i18n-scope.provider';
import { I18nScopedService } from '../../i18n/i18n-scoped.service';
import { ErrorService } from '../../services/error/error.service';
import {
  NotificationService,
  NotificationType,
} from '../../services/notification/notification-service';

export interface SectionManagerItem {
  id: string;
  key: string;
  label: string;
  visible: boolean;
  group?: string;
}

export interface SectionManagerDialogAdapter {
  loadItems(): Observable<SectionManagerItem[]>;
  createItem(dto: {
    key: string;
    label: string;
    order: number;
  }): Observable<SectionManagerItem>;
  updateItem(
    id: string,
    dto: { label?: string; visible?: boolean; order?: number; group?: string },
  ): Observable<unknown>;
  deleteItem(item: SectionManagerItem): Observable<void>;
  /** When provided, replaces the built-in confirmation dialog. Return true to proceed with deletion. */
  confirmDelete?: (item: SectionManagerItem) => Promise<boolean>;
}

export interface SectionManagerDialogConfig {
  adapter: SectionManagerDialogAdapter;
  hideKeys?: boolean;
  hideAdd?: boolean;
  hideDelete?: boolean;
  groupOptions?: string[];
}

interface WorkingSection {
  id: string;
  key: string;
  label: string;
  visible: boolean;
  group: string;
  originalLabel: string;
  originalVisible: boolean;
  originalOrder: number;
  originalGroup: string;
}

@Component({
  selector: 'app-section-manager-dialog',
  standalone: true,
  imports: [
    FormsModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatTooltipModule,
    I18nPipe,
    I18nScopeReadyDirective,
    GroupInputComponent,
  ],
  providers: [
    provideI18nScope('frontend/features/section-manager'),
    I18nScopedService,
  ],
  templateUrl: './section-manager-dialog.html',
  styleUrl: './section-manager-dialog.scss',
})
export class SectionManagerDialog implements OnInit {
  private static readonly ERROR_SOURCE = 'SectionManagerDialog';

  private readonly dialogRef = inject(MatDialogRef<SectionManagerDialog>);
  private readonly dialog = inject(MatDialog);
  private readonly notificationService = inject(NotificationService);
  private readonly i18n = inject(I18nScopedService);
  private readonly errorService = inject(ErrorService);
  private readonly config = inject<SectionManagerDialogConfig>(MAT_DIALOG_DATA);

  readonly hideKeys = this.config.hideKeys ?? false;
  readonly hideAdd = this.config.hideAdd ?? false;
  readonly hideDelete = this.config.hideDelete ?? false;
  readonly showGroupColumn = this.config.groupOptions !== undefined;
  readonly groupOptions = this.config.groupOptions ?? [];

  readonly sections = signal<WorkingSection[]>([]);
  readonly loading = signal(true);
  readonly saving = signal(false);

  newKey = signal('');
  newLabel = signal('');

  isDuplicate = computed(() => {
    const key = this.newKey().trim();
    const label = this.newLabel().trim();
    if (!this.hideKeys && !key && !label) return false;
    if (this.hideKeys && !label) return false;

    return this.sections().some(
      (s) => (!this.hideKeys && s.key === key) || s.label === label,
    );
  });

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.config.adapter.loadItems().subscribe({
      next: (items) => {
        this.sections.set(
          items.map((item, index) => ({
            id: item.id,
            key: item.key,
            label: item.label,
            visible: item.visible,
            group: item.group ?? '',
            originalLabel: item.label,
            originalVisible: item.visible,
            originalOrder: index,
            originalGroup: item.group ?? '',
          })),
        );
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  onDrop(event: CdkDragDrop<WorkingSection[]>): void {
    const items = [...this.sections()];
    moveItemInArray(items, event.previousIndex, event.currentIndex);
    this.sections.set(items);
  }

  onItemLabelChange(id: string, label: string): void {
    this.sections.update((sections) =>
      sections.map((s) => (s.id === id ? { ...s, label } : s)),
    );
  }

  onItemVisibleChange(id: string, visible: boolean): void {
    this.sections.update((sections) =>
      sections.map((s) => (s.id === id ? { ...s, visible } : s)),
    );
  }

  onItemGroupChange(id: string, group: string): void {
    this.sections.update((sections) =>
      sections.map((s) => (s.id === id ? { ...s, group } : s)),
    );
  }

  confirmDelete(item: WorkingSection): void {
    const proceed = this.config.adapter.confirmDelete
      ? this.config.adapter.confirmDelete(item)
      : firstValueFrom(
          this.dialog
            .open(ConfirmationDialog, {
              data: {
                title: this.i18n.t('confirm_delete'),
                message: this.i18n.t('confirm_delete_msg'),
              },
            })
            .afterClosed(),
        );

    void proceed.then((confirmed) => {
      if (confirmed) this.executeDelete(item);
    });
  }

  private executeDelete(item: WorkingSection): void {
    this.saving.set(true);
    this.config.adapter.deleteItem(item).subscribe({
      next: () => {
        this.sections.update((s) => s.filter((x) => x.id !== item.id));
        this.saving.set(false);
        this.notificationService.notify(
          this.i18n.t('section_deleted'),
          NotificationType.SUCCESS,
          2000,
        );
      },
      error: (error) => {
        this.saving.set(false);
        this.errorService.report(error, {
          source: SectionManagerDialog.ERROR_SOURCE,
          action: 'confirmDelete',
        });
      },
    });
  }

  addSection(): void {
    const key = this.newKey().trim();
    const label = this.newLabel().trim();
    if ((!this.hideKeys && !key) || !label || this.isDuplicate()) return;

    const order = this.sections().length;
    this.saving.set(true);
    this.config.adapter.createItem({ key, label, order }).subscribe({
      next: (created) => {
        this.sections.update((s) => [
          ...s,
          {
            id: created.id,
            key: created.key,
            label: created.label,
            visible: created.visible,
            group: created.group ?? '',
            originalLabel: created.label,
            originalVisible: created.visible,
            originalOrder: s.length,
            originalGroup: created.group ?? '',
          },
        ]);
        this.newKey.set('');
        this.newLabel.set('');
        this.saving.set(false);
        this.notificationService.notify(
          this.i18n.t('section_created'),
          NotificationType.SUCCESS,
          2000,
        );
      },
      error: (error) => {
        this.saving.set(false);
        this.errorService.report(error, {
          source: SectionManagerDialog.ERROR_SOURCE,
          action: 'addSection',
        });
      },
    });
  }

  save(): void {
    const items = this.sections();
    const updates = items
      .map((item, index) => ({ item, order: index }))
      .filter(
        ({ item, order }) =>
          item.label !== item.originalLabel ||
          item.visible !== item.originalVisible ||
          order !== item.originalOrder ||
          item.group !== item.originalGroup,
      );

    if (updates.length === 0) {
      this.dialogRef.close(true);
      return;
    }

    this.saving.set(true);
    let completed = 0;
    let hasError = false;

    const done = () => {
      completed++;
      if (completed === updates.length) {
        this.saving.set(false);
        if (!hasError) {
          this.notificationService.notify(
            this.i18n.t('section_saved'),
            NotificationType.SUCCESS,
            2000,
          );
          this.dialogRef.close(true);
        }
      }
    };

    for (const { item, order } of updates) {
      this.config.adapter
        .updateItem(item.id, {
          label: item.label,
          visible: item.visible,
          order,
          group: item.group,
        })
        .subscribe({
          next: done,
          error: (error) => {
            hasError = true;
            this.errorService.report(error, {
              source: SectionManagerDialog.ERROR_SOURCE,
              action: 'save',
            });
            done();
          },
        });
    }
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
